import { z } from "zod";
import { agentRunSchema } from "./agents";
import { githubPullRequestLinkSchema } from "./integrations";
import { actorTypeSchema, idSchema, isoDateTimeSchema, paginatedResponseSchema } from "./shared";

export const issueStatusKeySchema = z.enum([
  "triage",
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "blocked",
  "done",
  "canceled",
]);

export const issuePrioritySchema = z.enum(["none", "low", "medium", "high", "urgent"]);

export const issueTypeSchema = z.enum([
  "task",
  "bug",
  "feature",
  "research",
  "spike",
  "client_request",
  "internal_ops",
  "qa",
  "devops",
  "agent_task",
]);

export const issueSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  key: z.string().min(1),
  title: z.string().min(1),
  description: z.string().nullable(),
  status: issueStatusKeySchema,
  priority: issuePrioritySchema,
  type: issueTypeSchema,
  creatorUserId: idSchema,
  assigneeUserId: idSchema.nullable(),
  assigneeAgentId: idSchema.nullable(),
  chapterId: idSchema.nullable(),
  productId: idSchema.nullable(),
  clientAccountId: idSchema.nullable(),
  projectId: idSchema.nullable(),
  researchTrackId: idSchema.nullable(),
  cycleId: idSchema.nullable(),
  roadmapMilestoneId: idSchema.nullable(),
  parentIssueId: idSchema.nullable(),
  labels: z.array(idSchema).default([]),
  estimate: z.number().nullable(),
  dueDate: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const issueListFiltersSchema = z.object({
  status: issueStatusKeySchema.optional(),
  priority: issuePrioritySchema.optional(),
  assigneeUserId: idSchema.optional(),
  assigneeAgentId: idSchema.optional(),
  chapterId: idSchema.optional(),
  productId: idSchema.optional(),
  clientAccountId: idSchema.optional(),
  projectId: idSchema.optional(),
  researchTrackId: idSchema.optional(),
  cycleId: idSchema.optional(),
  roadmapMilestoneId: idSchema.optional(),
  q: z.string().optional(),
});

export const createIssueRequestSchema = z.object({
  title: z.string().trim().min(1),
  description: z.string().trim().optional(),
  priority: issuePrioritySchema.default("none"),
  type: issueTypeSchema.default("task"),
  status: issueStatusKeySchema.default("triage"),
  assigneeUserId: idSchema.nullable().optional(),
  assigneeAgentId: idSchema.nullable().optional(),
  chapterId: idSchema.nullable().optional(),
  productId: idSchema.nullable().optional(),
  clientAccountId: idSchema.nullable().optional(),
  projectId: idSchema.nullable().optional(),
  researchTrackId: idSchema.nullable().optional(),
  cycleId: idSchema.nullable().optional(),
  roadmapMilestoneId: idSchema.nullable().optional(),
  parentIssueId: idSchema.nullable().optional(),
  labelIds: z.array(idSchema).default([]),
  estimate: z.number().int().nonnegative().nullable().optional(),
  dueDate: isoDateTimeSchema.nullable().optional(),
});

export const updateIssueRequestSchema = createIssueRequestSchema.partial().extend({
  title: z.string().trim().min(1).optional(),
  priority: issuePrioritySchema.optional(),
  type: issueTypeSchema.optional(),
  status: issueStatusKeySchema.optional(),
  labelIds: z.array(idSchema).optional(),
});

export const changeIssueStatusRequestSchema = z.object({
  status: issueStatusKeySchema,
  blockedReason: z.string().trim().optional(),
  doneSummary: z.string().trim().optional(),
});

export const assignIssueRequestSchema = z.object({
  assigneeUserId: idSchema.nullable(),
});

export const assignAgentRequestSchema = z.object({
  assigneeAgentId: idSchema.nullable(),
});

export const commentSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  issueId: idSchema,
  body: z.string().min(1),
  actorType: actorTypeSchema,
  actorId: idSchema.nullable(),
  actorDisplayName: z.string().min(1).optional(),
  createdAt: isoDateTimeSchema,
});

export const createCommentRequestSchema = z.object({
  body: z.string().trim().min(1),
});

export const activityEventSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  type: z.string().min(1),
  actorType: actorTypeSchema,
  actorId: idSchema.nullable(),
  actorDisplayName: z.string().min(1).optional(),
  issueId: idSchema.nullable(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: isoDateTimeSchema,
});

export const artifactSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  issueId: idSchema,
  agentRunId: idSchema.nullable(),
  type: z.enum(["pull_request", "commit", "log", "test_result", "screenshot", "file", "note"]),
  title: z.string().min(1),
  url: z.string().url().nullable(),
  body: z.string().nullable(),
  createdAt: isoDateTimeSchema,
});

export const createArtifactRequestSchema = artifactSchema
  .pick({ type: true, title: true, url: true, body: true })
  .extend({ agentRunId: idSchema.optional() });

export const issueDetailResponseSchema = z.object({
  issue: issueSchema,
  comments: z.array(commentSchema),
  activity: z.array(activityEventSchema),
  artifacts: z.array(artifactSchema),
  agentRuns: z.array(agentRunSchema).default([]),
  githubPullRequests: z.array(githubPullRequestLinkSchema).default([]),
});

export const issueListResponseSchema = paginatedResponseSchema(issueSchema);

export type CreateIssueRequest = z.infer<typeof createIssueRequestSchema>;
export type Issue = z.infer<typeof issueSchema>;
export type IssuePriority = z.infer<typeof issuePrioritySchema>;
export type IssueType = z.infer<typeof issueTypeSchema>;
